import React, { useState, useCallback, useRef, useEffect } from 'react';
import { Box, IconButton, Card, CardMedia, Grid, Typography } from '@mui/material';
import { ArrowBackIos, ArrowForwardIos, ZoomIn } from '@mui/icons-material';
import { trackGallerySwipe } from '../../services/analyticsService';
import { getThumbOrOriginal as sharedThumbOrOriginal, markThumbFailed as markThumbFailedShared } from '../../utils/thumbImage';
import { getCropImageStyle } from '../../utils/imageCrop';


// MÓDOSÍTVA: imageSx, imageClassName és ÚJ: mainImageAspectRatio propok fogadása
// useThumbnails: a főképhez is a kis WebP thumbnailt használja (listakártyák); priority: eager betöltés (LCP)
const ImageGallery = ({ images, providerName, providerId = null, showThumbnails = true, showCaption = true, imageSx = {}, imageClassName = '', mainImageAspectRatio = '56.25%', containerSx = {}, onImageClick, useThumbnails = false, priority = false }) => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  // Hiányzó thumb (régi képek, backfill előtt): a közös, munkamenetnyi cache-be kerül (utils/thumbImage.js), így
  // a többi kártya már egyből az eredetit kéri. A helyi számláló csak ezt a komponenst rajzolja újra.
  const [, setThumbFailures] = useState(0);
  const markThumbFailed = useCallback((originalUrl) => {
    markThumbFailedShared(originalUrl);
    setThumbFailures((n) => n + 1);
  }, []);
  const thumbnailStripRef = useRef(null);
  const activeThumbnailRef = useRef(null);
  const touchStartX = useRef(null);

  // Meghatározzuk a ténylegesen megjelenítendő képeket
  // Ha nincsenek képek, vagy üres a tömb, akkor az alapértelmezett képet használjuk
  const effectiveImages = (images && images.length > 0)
    ? images
    : [{
        id: 'default-image', // Egyedi azonosító az alapértelmezett képnek
        imageUrl: '/images/default-provider.jpg', // AZ ALAPÉRTELMEZETT KÉP ELÉRÉSI ÚTJA
        title: 'Nincs feltöltött kép',
      }];

  // Frissítjük a currentImageIndex-et, ha az effectiveImages hossza megváltozik
  // (pl. ha átváltunk alapértelmezett képre, vagy fordítva)
  useEffect(() => {
    if (currentImageIndex >= effectiveImages.length) {
      setCurrentImageIndex(0);
    }
  }, [effectiveImages, currentImageIndex]);

  // Segédfüggvény a kép URL felépítéséhez
  const getImageUrl = (image) => {
    if (image.id === 'default-image') {
      // Ha ez az alapértelmezett kép (amit mi hoztunk létre a kódban)
      return image.imageUrl; // Pl.: /images/default-provider.jpg -> ezt a frontend szerverről kéri
    }
    // MÓDOSÍTVA: Az imageUrl már a teljes, publikus R2 URL-t tartalmazza
    return image.imageUrl;
  };

  // Thumbnail URL, ha van (és nem hibás); különben az eredeti kép
  const getThumbOrOriginal = (image) => {
    const original = getImageUrl(image);
    if (image.id === 'default-image') return original;
    return sharedThumbOrOriginal(original);
  };

  const handleNext = useCallback(() => {
    setCurrentImageIndex((prevIndex) => {
      if (prevIndex >= effectiveImages.length - 1) return prevIndex;
      if (providerId) trackGallerySwipe(providerId, 'next');
      return prevIndex + 1;
    });
  }, [effectiveImages.length, providerId]);

  const handlePrev = useCallback(() => {
    setCurrentImageIndex((prevIndex) => {
      if (prevIndex <= 0) return prevIndex;
      if (providerId) trackGallerySwipe(providerId, 'prev');
      return prevIndex - 1;
    });
  }, [providerId]);

  const handleThumbnailClick = useCallback((index) => {
    setCurrentImageIndex(index);
  }, []);

  const handleTouchStart = useCallback((e) => {
    touchStartX.current = e.touches[0].clientX;
  }, []);

  const handleTouchEnd = useCallback((e) => {
    if (touchStartX.current === null) return;
    const delta = e.changedTouches[0].clientX - touchStartX.current;
    touchStartX.current = null;
    if (Math.abs(delta) < 50) return;
    if (delta < 0) handleNext();
    else handlePrev();
  }, [handleNext, handlePrev]);

  // Csak a miniatűr sávot görgetjük vízszintesen — a scrollIntoView az egész
  // oldalt is legörgette (mountkor is), így a profil tetején lévő név eltűnt.
  useEffect(() => {
    const strip = thumbnailStripRef.current;
    const thumb = activeThumbnailRef.current;
    if (!strip || !thumb) return;
    const target = thumb.offsetLeft - (strip.clientWidth - thumb.offsetWidth) / 2;
    strip.scrollTo({ left: Math.max(0, target), behavior: 'smooth' });
  }, [currentImageIndex]);

  const currentImage = effectiveImages[currentImageIndex];

  return (
    <Box sx={{ position: 'relative', width: '100%', mb: 2 }}>
      {/* MÓDOSÍTVA: Card eltávolítva, csak Box marad */}
      <Box
        sx={{
          position: 'relative',
          width: '100%',
          paddingBottom: mainImageAspectRatio !== 'auto' ? mainImageAspectRatio : 0,
          // Halvány helykitöltő, amíg a kép betölt — átlátszóval a szekció háttere látszott át (görgetéskori sávok)
          backgroundColor: 'grey.100',
          height: mainImageAspectRatio === 'auto' ? 'auto' : undefined,
          display: mainImageAspectRatio === 'auto' ? 'flex' : undefined,
          justifyContent: mainImageAspectRatio === 'auto' ? 'center' : undefined,
          alignItems: mainImageAspectRatio === 'auto' ? 'center' : undefined,
          borderRadius: '16px 16px 0 0',
          overflow: 'hidden', // MÓDOSÍTVA: Overflow itt
          ...containerSx, // ÚJ: Egyedi container stílusok
        }}
      >
        {/* Kép wrapper – kattintható ha onImageClick van, swipe-olható */}
        <Box
          onClick={onImageClick ? () => onImageClick(currentImageIndex) : undefined}
          onTouchStart={effectiveImages.length > 1 ? handleTouchStart : undefined}
          onTouchEnd={effectiveImages.length > 1 ? handleTouchEnd : undefined}
          sx={{
            position: mainImageAspectRatio !== 'auto' ? 'absolute' : 'relative',
            top: mainImageAspectRatio !== 'auto' ? 0 : undefined,
            left: mainImageAspectRatio !== 'auto' ? 0 : undefined,
            width: '100%',
            height: mainImageAspectRatio !== 'auto' ? '100%' : 'auto',
            cursor: onImageClick ? 'zoom-in' : 'default',
            // A kivágás (nagyítás, fókusz) a keret méretéből számol: a kép a keret belsejében van
            containerType: mainImageAspectRatio !== 'auto' ? 'size' : undefined,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            overflow: 'hidden',
            '&:hover .zoom-hint': onImageClick ? { opacity: 1 } : {},
          }}
        >
          <CardMedia
            component="img"
            image={useThumbnails ? getThumbOrOriginal(currentImage) : getImageUrl(currentImage)}
            alt={currentImage.title || `${providerName} - Kép ${currentImageIndex + 1}`}
            className={imageClassName}
            loading={priority ? 'eager' : 'lazy'}
            decoding="async"
            onError={useThumbnails ? () => markThumbFailed(getImageUrl(currentImage)) : undefined}
            sx={{
              width: '100%',
              height: mainImageAspectRatio !== 'auto' ? '100%' : 'auto',
              objectFit: 'contain',
              objectPosition: 'center',
              display: 'block',
              // A szolgáltató kivágása (fókusz + nagyítás); régi képnél a közép, mint eddig
              ...getCropImageStyle(currentImage, { container: mainImageAspectRatio !== 'auto' }),
              ...imageSx,
            }}
          />
          {/* Zoom hint overlay */}
          {onImageClick && (
            <Box
              className="zoom-hint"
              sx={{
                position: 'absolute',
                top: 8,
                right: 8,
                bgcolor: 'rgba(0,0,0,0.45)',
                borderRadius: '50%',
                width: 36,
                height: 36,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                opacity: 0,
                transition: 'opacity 0.2s',
                pointerEvents: 'none',
              }}
            >
              <ZoomIn sx={{ color: 'white', fontSize: 20 }} />
            </Box>
          )}
        </Box>
        
        {/* Navigációs gombok */}
        {effectiveImages.length > 1 && (
          <>
            {/* MÓDOSÍTVA: Bal nyíl - padding-left fix */}
            {currentImageIndex > 0 && (
              <IconButton
                onClick={(e) => { e.stopPropagation(); handlePrev(); }}
                sx={{
                  position: 'absolute',
                  top: '50%',
                  left: 8,
                  transform: 'translateY(-50%)',
                  bgcolor: 'rgba(0, 0, 0, 0.1)',
                  color: 'white',
                  width: 40,
                  height: 40,
                  padding: 0, // MÓDOSÍTVA: Padding nullázása
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' },
                  zIndex: 1,
                }}
              >
                <ArrowBackIos sx={{ fontSize: 18, position: 'relative', left: 3 }} /> {/* MÓDOSÍTVA: Relative left */}
              </IconButton>
            )}

            {currentImageIndex < effectiveImages.length - 1 && (
              <IconButton
                onClick={(e) => { e.stopPropagation(); handleNext(); }}
                sx={{
                  position: 'absolute',
                  top: '50%',
                  right: 8,
                  transform: 'translateY(-50%)',
                  bgcolor: 'rgba(0,0,0,0.1)',
                  color: 'white',
                  width: 40,
                  height: 40,
                  padding: 0, // MÓDOSÍTVA: Padding nullázása
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' },
                  zIndex: 1,
                }}
              >
                <ArrowForwardIos sx={{ fontSize: 18 }} />
              </IconButton>
            )}
          </>
        )}

        {/* Lap-pöttyök – aktuális kép jelzése, kép alján középen */}
        {effectiveImages.length > 1 && (
          <Box
            sx={{
              position: 'absolute',
              left: '50%',
              transform: 'translateX(-50%)',
              bottom: showThumbnails ? { xs: 58, sm: 78 } : 10,
              display: 'flex',
              alignItems: 'center',
              gap: 0.75,
              zIndex: 2,
              px: 1,
              py: 0.5,
            }}
          >
            {effectiveImages.map((image, index) => (
              <Box
                key={`dot-${image.id ?? index}`}
                onClick={(e) => { e.stopPropagation(); handleThumbnailClick(index); }}
                sx={{
                  width: index === currentImageIndex ? 7 : 6,
                  height: index === currentImageIndex ? 7 : 6,
                  borderRadius: '50%',
                  cursor: 'pointer',
                  bgcolor: index === currentImageIndex ? '#ffffff' : 'rgba(255,255,255,0.5)',
                  boxShadow: '0 0 3px rgba(0,0,0,0.6)',
                  transition: 'background-color 0.2s ease, width 0.2s ease, height 0.2s ease',
                }}
              />
            ))}
          </Box>
        )}

        {/* Lebegő miniatűr sáv - változatlan */}
        {showThumbnails && effectiveImages.length > 1 && (
          <Box
            ref={thumbnailStripRef}
            sx={{
              position: 'absolute',
              bottom: 0,
              left: 0,
              right: 0,
              width: '100%',
              bgcolor: 'transparent',
              p: { xs: 0.5, sm: 1 },
              zIndex: 2,
              display: 'flex',
              overflowX: 'auto',
              whiteSpace: 'nowrap',
              justifyContent: 'center',
              '&::-webkit-scrollbar': { display: 'none' },
              msOverflowStyle: 'none',
              scrollbarWidth: 'none',
            }}
          >
            {effectiveImages.map((image, index) => (
              <Box
                key={image.id}
                onClick={(e) => { e.stopPropagation(); handleThumbnailClick(index); }}
                ref={index === currentImageIndex ? activeThumbnailRef : null}
                sx={{
                  flexShrink: 0,
                  width: { xs: 60, sm: 80 },
                  height: { xs: 45, sm: 60 },
                  marginRight: { xs: 0.5, sm: 1 },
                  cursor: 'pointer',
                  border: index === currentImageIndex ? '2px solid #D4A574' : '2px solid transparent',
                  borderRadius: '4px',
                  overflow: 'hidden',
                  transition: 'border-color 0.3s ease, opacity 0.3s ease',
                  opacity: index === currentImageIndex ? 1 : 0.6,
                  '&:hover': { borderColor: '#B8860B', opacity: 1 },
                }}
              >
                <img
                  src={getThumbOrOriginal(image)}
                  alt={image.title || `Miniatűr ${index + 1}`}
                  loading="lazy"
                  decoding="async"
                  onError={() => markThumbFailed(getImageUrl(image))}
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    display: 'block',
                  }}
                />
              </Box>
            ))}
          </Box>
        )}
      </Box>

      {/* Kép felirata (pl. forrás megjelölés) - csak ha van megadva */}
      {showCaption && currentImage.description && (
        <Typography
          variant="caption"
          sx={{
            display: 'block',
            mt: 0.5,
            px: 0.5,
            color: 'text.secondary',
            fontStyle: 'italic',
            fontSize: '0.7rem',
            textAlign: 'center',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {currentImage.description}
        </Typography>
      )}
    </Box>
  );
};

export default ImageGallery;